import { ApplyCouponConfig } from "../../interfaces/coupon/ApplyCouponConfig";
import ThunkAction from "../../interfaces/ThunkAction";
import Cart from "../../interfaces/cart/Cart";
import HttpUtil from "../../utils/HttpUtil";
import CartCouponClient from "../../clients/cart/CartCouponClient";
import { setCart } from "./cartActions";
import GlobalState from "../../interfaces/GlobalState";
import { loadPaymentGateways } from "./cartPaymentActions";
import PaymentGatewayIdEnum from "../../enums/payment/PaymentGatewayIdEnum";
import { loadShippingQuotes } from "./cartShippingActions";
import eventEmitter from "../../events/eventEmitter";
import CartCouponEnteredEvent from "../../events/events/CartCouponEnteredEvent";
import CartCouponAppliedEvent from "../../events/events/CartCouponAppliedEvent";
import CartCouponDeniedEvent from "../../events/events/CartCouponDeniedEvent";
import CartCouponRemovedEvent from "../../events/events/CartCouponRemovedEvent";
import Coupon from "../../interfaces/coupon/Coupon";

// ------------------------- [ Thunks ] -------------------------

const cartCouponClient = new CartCouponClient();

export const applyCoupon = (config: ApplyCouponConfig): ThunkAction<Promise<Cart>> => (
    async (dispatch, getState: () => GlobalState) => {
        eventEmitter.emit(new CartCouponEnteredEvent(config));
        try {
            const { gateways } = getState().payment;
            const beforeFreeShippingIsEligible = getState().cart?.free_shipping_eligibility.is_eligible ?? false;
            const response = await cartCouponClient.applyCoupon(config);

            dispatch(setCart(response.data));
            eventEmitter.emit(new CartCouponAppliedEvent(config));

            // When Braintree has been set previously and no longer needed as a payment method
            if (response.data.totals.grand <= 0 && gateways[0]) {
                await dispatch(loadPaymentGateways(config.cart_uuid));
            }

            // Coupon might affect shipping quotes.
            if (response.data.free_shipping_eligibility.is_eligible !== beforeFreeShippingIsEligible && getState().shippingQuotes.length) {
                await dispatch(loadShippingQuotes(config.cart_uuid));
            }

            return response.data;
        } catch (error) {
            const errorMessage = "Failed to apply coupon.";
            eventEmitter.emit(new CartCouponDeniedEvent(config));
            HttpUtil.logErroneousRequest(errorMessage, error);

            throw error;
        }
    }
);

export const removeCoupon = (cartUuid: string, coupon: Coupon): ThunkAction<Promise<Cart>> => (
    async (dispatch, getState: () => GlobalState) => {
        const { coupon_id, code } = coupon;
        try {
            const response = await cartCouponClient.removeCoupon(cartUuid, coupon_id);
            const beforeFreeShippingIsEligible = getState().cart?.free_shipping_eligibility.is_eligible ?? false;
            const { gateways } = getState().payment;

            dispatch(setCart(response.data));

            // When Braintree has been set previously and no longer needed as a payment method
            if (response.data.totals.grand > 0 && gateways[0]?.payment_gateway_id === PaymentGatewayIdEnum.FREE) {
                await dispatch(loadPaymentGateways(cartUuid));
            }

            // Coupons might affect shipping quotes.
            if (response.data.free_shipping_eligibility.is_eligible !== beforeFreeShippingIsEligible && getState().shippingQuotes.length) {
                await dispatch(loadShippingQuotes(cartUuid));
            }

            eventEmitter.emit(new CartCouponRemovedEvent({ cart_uuid: cartUuid, code }));

            return response.data;
        } catch (error) {
            const errorMessage = "Failed to remove coupon.";
            HttpUtil.logErroneousRequest(errorMessage, error);

            throw error;
        }
    }
);
